/**
 * Aurora DataTable — generieke admin-tabel met:
 *  - Sortable kolommen (per-col of via `sortable` prop op alle)
 *  - Client-side search (substring over alle kolom-values)
 *  - Pagination (client-side, met page-size dropdown)
 *  - CSV-export van de huidige gefilterde + gesorteerde rij-set
 *  - Optionele row-numbers en row-selectie via checkboxes
 *  - Server-mode: `serverPagination` + `serverSearch` laten de footer en
 *    het zoekveld op server-meta draaien (zelfde uiterlijk als client-side;
 *    de consumer vertaalt callbacks naar query-params)
 *  - Uitklapbare rijen via `expandable` (chevron-kolom + detail-paneel)
 *
 * Renderbare cellen worden via `Column.render(value, row)` opgegeven;
 * voor sort/search gebruikt de tabel het ruwe value uit `row[col.key]`
 * — dus zorg dat `key` op de raw veld-naam wijst, niet op het gerenderde
 * resultaat. Voor afgeleide kolommen (bv. format-name) kun je `key` op
 * een unique string zetten en `searchAccessor` meegeven.
 */
import React, { useMemo, useState, useEffect, useRef } from "react";
import { CaretUp, CaretDown, CaretUpDown, CaretRight, MagnifyingGlass, DownloadSimple } from "@phosphor-icons/react";
import styles from "./DataTable.module.css";

export interface Column<T> {
  key: string;
  label: string;
  sortable?: boolean;
  render?: (value: any, row: T) => React.ReactNode;
  /**
   * Override voor sort/search/export — gebruik als `key` niet 1:1 op
   * een ruw veld wijst (bv. computed display-name).
   */
  searchAccessor?: (row: T) => string;
  sortAccessor?: (row: T) => string | number | null | undefined;
  /** Sluit deze kolom uit van CSV-export (bv. actions). */
  exportable?: boolean;
  width?: string;
  /**
   * Optionele inline filter-control onder de kolom-header. DataTable
   * rendert 'm in een tweede header-rij zodra ÉÉN of meer kolommen
   * een headerFilter hebben. De control is volledig controlled door
   * de consumer — DataTable doet niets met de waarde. Typisch een
   * `<input>` of een eigen combobox die naar parent-state schrijft
   * en (bij server-side data) naar de URL syncset.
   */
  headerFilter?: React.ReactNode;
}

/**
 * Labels-bundle voor i18n. Wordt door de consumer geleverd zodat
 * DataTable zelf geen react-i18next-dependency heeft. Strings volgen
 * onze `{placeholder}`-conventie.
 */
export interface DataTableLabels {
  searchPlaceholder?: string;
  empty?: string;          // "Geen rijen om te tonen."
  noMatch?: string;        // "Geen resultaten voor deze zoekopdracht."
  range?: string;          // "{from}–{to} van {total}"
  pageSize?: string;       // "Per pagina"
  pageOf?: string;         // "Pagina {current} van {total}"
  exportLabel?: string;    // "Exporteer CSV"
  exportHint?: string;     // "Alle gefilterde rijen…"
  rowNumberHeader?: string; // "#"
  selectAll?: string;       // aria
  selectRow?: string;       // aria, with {n} placeholder
  sortAsc?: string;         // aria (sort direction)
  scrollRegion?: string;    // aria, op het scroll-gebied als de tabel niet past
  sortDesc?: string;        // aria
  // LOKALE toevoeging (issue 294): de pijl-knoppen van de paginering hadden een
  // vaste Engelse naam en stonden daardoor buiten bereik van elke consumer.
  previousPage?: string;    // aria, "Vorige pagina"
  nextPage?: string;        // aria, "Volgende pagina"
}

/**
 * Server-side paginering: de tabel toont de meegegeven pagina en rendert
 * dezelfde footer als client-side, maar op basis van server-meta. Page is
 * 1-based (zoals Laravel's paginator).
 */
export interface ServerPagination {
  page: number;
  lastPage: number;
  perPage: number;
  total: number;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
}

/** Server-side zoeken: controlled zoekveld; de consumer debounced zelf. */
export interface ServerSearch {
  value: string;
  onChange: (query: string) => void;
}

/** Uitklapbare rijen: chevron-kolom + detail-paneel onder de rij. */
export interface ExpandableRows<T> {
  isExpandable: (row: T) => boolean;
  renderExpanded: (row: T) => React.ReactNode;
  /** Stabiele sleutel per rij (bv. het record-id) voor de expanded-set. */
  rowKey: (row: T) => string;
  expandLabel?: string;   // aria, "Toon details"
  collapseLabel?: string; // aria, "Verberg details"
}

interface DataTableProps<T extends Record<string, any>> {
  columns: Column<T>[];
  data: T[];
  /** Alle kolommen sortable. Per-col `sortable` overschrijft dit. */
  sortable?: boolean;
  selectable?: boolean;
  searchable?: boolean;
  /** Aantal rijen per pagina. `null` of `0` = geen paginatie. */
  pageSize?: number | null;
  pageSizeOptions?: number[];
  exportable?: boolean;
  exportFilename?: string;
  showRowNumbers?: boolean;
  onSort?: (key: string, direction: "asc" | "desc") => void;
  /**
   * LOKALE toevoeging (#228 follow-up) — optionele, backwards-compatibele
   * persist-hooks voor de INTERNE (client-side) sort + search, zodat een
   * consumer de state naar de URL/sessionStorage kan syncen en na terug-navigatie
   * kan herstellen. Puur additief: laat je alles weg, dan gedraagt de tabel zich
   * exact als voorheen (uncontrolled, seed = leeg/ongesorteerd).
   *
   * - `initialSort` seedt de begin-sortering (alleen bij mount gelezen).
   * - `onSortChange` vuurt bij elke sort-wissel: het nieuwe sort-object, of
   *   `null` als de derde klik de sortering opheft.
   * - `initialSearch` seedt de begin-zoekterm (alleen bij mount gelezen).
   * - `onSearchChange` vuurt bij elke wijziging van de client-zoekterm.
   *
   * NB: deze slaan op de CLIENT-mode. In server-mode (`serverSearch`) blijft het
   * zoekveld door `serverSearch` bestuurd; `onSortChange` werkt naast `onSort`.
   * Kandidaat om te upstreamen naar het DS-package (zie AURORA-NOTES.md).
   */
  initialSort?: { key: string; dir: "asc" | "desc" };
  onSortChange?: (sort: { key: string; dir: "asc" | "desc" } | null) => void;
  initialSearch?: string;
  onSearchChange?: (query: string) => void;
  onSelectionChange?: (selectedRows: T[]) => void;
  labels?: DataTableLabels;
  /** Server-mode paginering; sluit client-side slicing uit. */
  serverPagination?: ServerPagination;
  /** Server-mode zoeken; sluit het client-side filter uit. */
  serverSearch?: ServerSearch;
  /** Uitklapbare rijen. */
  expandable?: ExpandableRows<T>;
}

/**
 * Standaard-labels (Engels) als fallback wanneer consumer geen labels meegeeft.
 *
 * ⚠️ GEËXPORTEERD OMDAT HET DE ENIGE RUNTIME-LIJST VAN ALLE SLEUTELS IS (issue 643).
 * `DataTableLabels` is een TypeScript-interface en bestaat niet meer zodra de code
 * draait; dit object is `Required<…>` en heeft er dus per definitie precies één
 * sleutel per label. `useDataTableLabels` wordt daartegen gecontroleerd, zodat een
 * nieuw label niet stil in het Engels kan blijven staan.
 */
export const DEFAULT_LABELS: Required<DataTableLabels> = {
  searchPlaceholder: "Search…",
  empty: "No data to display.",
  noMatch: "No results match your search.",
  range: "{from}–{to} of {total}",
  pageSize: "Per page",
  pageOf: "Page {current} of {total}",
  exportLabel: "Export CSV",
  exportHint: "All filtered rows, in current sort order. Actions column is omitted.",
  rowNumberHeader: "#",
  selectAll: "Select all rows",
  selectRow: "Select row {n}",
  sortAsc: "sorted ascending",
  sortDesc: "sorted descending",
  scrollRegion: "Table, scrollable sideways",
  previousPage: "Previous page",
  nextPage: "Next page",
};

/** Eenvoudige template-vervang voor "{key}"-placeholders. */
function fmt(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? `{${k}}`));
}

/** Pak een sort-/search-able waarde uit een rij via accessor of key. */
function rawValue<T>(row: T, col: Column<T>, mode: "sort" | "search"): unknown {
  if (mode === "sort" && col.sortAccessor) return col.sortAccessor(row);
  if (mode === "search" && col.searchAccessor) return col.searchAccessor(row);
  return (row as Record<string, unknown>)[col.key];
}

/** RFC-4180-compatibele CSV-escape voor één veld. */
function csvField(value: unknown): string {
  if (value == null) return "";
  const s = String(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function DataTable<T extends Record<string, any>>({
  columns,
  data,
  sortable = false,
  selectable = false,
  searchable = false,
  pageSize: pageSizeProp = null,
  pageSizeOptions = [10, 25, 50, 100],
  exportable = false,
  exportFilename = "export.csv",
  showRowNumbers = false,
  onSort,
  initialSort,
  onSortChange,
  initialSearch,
  onSearchChange,
  onSelectionChange,
  labels: labelsProp,
  serverPagination,
  serverSearch,
  expandable,
}: DataTableProps<T>) {
  const labels = { ...DEFAULT_LABELS, ...labelsProp };

  /**
   * Past de tabel niet, dan moet je erbij kunnen — ook zonder muis (issue 635).
   *
   * Bewust géén `ResizeObserver`: jsdom kent hem niet, dus dat zou elke test van
   * elke tabel een stub laten meeslepen voor een geval dat zich alleen in een
   * echte browser voordoet. Meten bij mount en bij een venster-resize dekt wat er
   * in de praktijk gebeurt — iemand maakt zijn venster smaller, of opent de app op
   * een kleiner scherm.
   *
   * In jsdom zijn `scrollWidth` en `clientWidth` allebei 0, dus dit blijft daar
   * `false` en verandert er niets aan de bestaande tests. Dat is geen toeval maar
   * de reden dat de vergelijking strikt is (`>`, niet `>=`).
   */
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [scrollable, setScrollable] = useState(false);

  useEffect(() => {
    const element = scrollerRef.current;

    if (element === null) {
      return;
    }

    const measure = () => setScrollable(element.scrollWidth > element.clientWidth);

    measure();
    window.addEventListener('resize', measure);

    return () => window.removeEventListener('resize', measure);
    // Kolommen erbij of rijen eruit verandert de breedte, dus dan opnieuw meten.
  }, [columns.length, data.length]);

  // Seed sort/search uit de optionele `initial*`-props (alleen bij mount). Zonder
  // props valt dit terug op het oude gedrag (null / lege string).
  const [sortKey, setSortKey] = useState<string | null>(initialSort?.key ?? null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | null>(initialSort?.dir ?? null);
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState(initialSearch ?? "");
  const [pageSize, setPageSize] = useState<number | null>(pageSizeProp);
  const [pageIndex, setPageIndex] = useState(0);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  // Reset naar pagina 0 als de data of het filter wezenlijk verandert.
  // Anders zou je op pagina 3 kunnen blijven hangen op een 2-rijen-result.
  // In server-mode beheert de consumer de pagina zelf.
  useEffect(() => {
    if (!serverPagination) setPageIndex(0);
  }, [searchQuery, data.length, serverPagination]);

  /* ---- Filter ---- */

  const filteredData = useMemo(() => {
    // Server-mode: het filter draait op de server; toon wat binnenkomt.
    if (serverSearch || !searchable || searchQuery.trim() === "") return data;
    const q = searchQuery.toLowerCase();
    return data.filter((row) =>
      columns.some((col) => {
        const v = rawValue(row, col, "search");
        if (v == null) return false;
        return String(v).toLowerCase().includes(q);
      }),
    );
  }, [data, columns, searchQuery, searchable, serverSearch]);

  /* ---- Sort ---- */

  const sortedData = useMemo(() => {
    if (!sortKey || !sortDirection || onSort) return filteredData;
    const col = columns.find((c) => c.key === sortKey);
    if (!col) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aVal = rawValue(a, col, "sort");
      const bVal = rawValue(b, col, "sort");

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      const comparison =
        typeof aVal === "string" && typeof bVal === "string"
          ? aVal.localeCompare(bVal)
          : Number(aVal) - Number(bVal);

      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [filteredData, columns, sortKey, sortDirection, onSort]);

  /* ---- Paginate ---- */
  // Server-mode: `data` IS de huidige pagina; footer-teksten komen uit de
  // server-meta zodat het uiterlijk identiek is aan client-side paginering.

  const totalPages = serverPagination
    ? Math.max(1, serverPagination.lastPage)
    : pageSize
      ? Math.max(1, Math.ceil(sortedData.length / pageSize))
      : 1;
  const safePageIndex = serverPagination
    ? serverPagination.page - 1
    : Math.min(pageIndex, totalPages - 1);
  const pagedData = !serverPagination && pageSize
    ? sortedData.slice(safePageIndex * pageSize, (safePageIndex + 1) * pageSize)
    : sortedData;
  const totalRows = serverPagination ? serverPagination.total : sortedData.length;
  const firstShown = serverPagination
    ? (totalRows === 0 ? 0 : (serverPagination.page - 1) * serverPagination.perPage + 1)
    : sortedData.length === 0 ? 0 : safePageIndex * (pageSize ?? sortedData.length) + 1;
  const lastShown = serverPagination
    ? Math.min(serverPagination.page * serverPagination.perPage, totalRows)
    : pageSize
      ? Math.min((safePageIndex + 1) * pageSize, sortedData.length)
      : sortedData.length;

  /* ---- Sort handler ---- */

  const handleSort = (key: string) => {
    let nextDirection: "asc" | "desc" | null;
    if (sortKey !== key) nextDirection = "asc";
    else if (sortDirection === "asc") nextDirection = "desc";
    else nextDirection = null;
    setSortKey(nextDirection ? key : null);
    setSortDirection(nextDirection);
    if (nextDirection && onSort) onSort(key, nextDirection);
    // LOKALE (#228 follow-up): meld de nieuwe client-sort (of `null` bij opheffen)
    // zodat een consumer 'm kan persisteren. No-op als de prop ontbreekt.
    onSortChange?.(nextDirection ? { key, dir: nextDirection } : null);
  };

  // Client-search-wijziging: update de interne state én meld 'm optioneel (LOKALE
  // #228 follow-up). In server-mode gaat de wijziging via `serverSearch.onChange`.
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    onSearchChange?.(value);
  };

  /* ---- Selection ---- */

  const toggleRow = (originalIdx: number) => {
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(originalIdx)) next.delete(originalIdx);
      else next.add(originalIdx);
      onSelectionChange?.(data.filter((_, i) => next.has(i)));
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIndices.size === data.length) {
      setSelectedIndices(new Set());
      onSelectionChange?.([]);
    } else {
      const all = new Set(data.map((_, i) => i));
      setSelectedIndices(all);
      onSelectionChange?.([...data]);
    }
  };

  const allSelected = data.length > 0 && selectedIndices.size === data.length;

  /* ---- CSV export ---- */

  const handleExport = () => {
    const exportCols = columns.filter((c) => c.exportable !== false);
    const headerLine = exportCols.map((c) => csvField(c.label)).join(",");
    const rowLines = sortedData.map((row) =>
      exportCols
        .map((c) => {
          const v = rawValue(row, c, "search");
          return csvField(v);
        })
        .join(","),
    );
    // Prepend UTF-8 BOM zodat Excel non-ASCII karakters herkent, plus
    // een `sep=,`-hint zodat Excel-NL (default delimiter `;` op een
    // Nederlandse Windows-installatie) onze comma-separated CSV niet
    // als één-kolom-tekst opent. Excel-NL/EN/Mac honoreren deze hint
    // en verbergen de regel; LibreOffice negeert 'm netjes.
    const csv = "﻿sep=,\r\n" + [headerLine, ...rowLines].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = exportFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  /* ---- Render helpers ---- */

  const renderSortIndicator = (col: Column<T>) => {
    const isSortable = sortable || col.sortable;
    if (!isSortable) return null;
    if (sortKey === col.key && sortDirection === "asc") {
      return <span className={styles.sortIcon} aria-label={labels.sortAsc}><CaretUp size={14} weight="fill" /></span>;
    }
    if (sortKey === col.key && sortDirection === "desc") {
      return <span className={styles.sortIcon} aria-label={labels.sortDesc}><CaretDown size={14} weight="fill" /></span>;
    }
    // Idle: subtiele dubbel-caret zodat de kolom als sortable herkenbaar is.
    return <span className={`${styles.sortIcon} ${styles.sortIconIdle}`} aria-hidden="true"><CaretUpDown size={14} weight="regular" /></span>;
  };

  const totalCols =
    columns.length + (selectable ? 1 : 0) + (showRowNumbers ? 1 : 0) + (expandable ? 1 : 0);

  const toggleExpanded = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className={styles.wrapper}>
      {(searchable || exportable) && (
        <div className={styles.toolbar}>
          {searchable && (
            <div className={styles.searchWrap}>
              <span className={styles.searchIcon} aria-hidden="true">
                <MagnifyingGlass size={16} weight="regular" />
              </span>
              {/* ⚠️ `aria-label` en niet alleen `placeholder` (issue 551). Een
                  placeholder is geen naam: hij verdwijnt zodra iemand typt, en
                  een schermlezer kondigde dit veld aan als "zoeken", zonder
                  meer — in elke tabel in de app dezelfde naamloze rij. */}
              <input
                type="search"
                className={styles.searchInput}
                aria-label={labels.searchPlaceholder}
                placeholder={labels.searchPlaceholder}
                value={serverSearch ? serverSearch.value : searchQuery}
                onChange={(e) => (serverSearch ? serverSearch.onChange(e.target.value) : handleSearchChange(e.target.value))}
              />
            </div>
          )}
          <div className={styles.toolbarSpacer} />
          {exportable && (
            <button
              type="button"
              className={styles.exportBtn}
              onClick={handleExport}
              title={labels.exportHint}
            >
              <DownloadSimple size={16} weight="regular" />
              {labels.exportLabel}
            </button>
          )}
        </div>
      )}

      {/* ⚠️ HET SCROLL-GEBIED ZIT HIER EN NIET OP `.wrapper`. Die heeft `overflow:
          hidden` voor de ronde hoeken, en daardoor viel een te brede tabel gewoon
          weg — inclusief de actie-knoppen, want die staan in de laatste kolom
          (issue 635). Het scrollen hoort ook níet om de werkbalk heen: zoeken en
          exporteren horen op hun plek te blijven staan terwijl de kolommen
          schuiven. */}
      <div
        ref={scrollerRef}
        className={styles.scroller}
        // Alleen een tabstop wanneer er écht iets te scrollen valt. Een
        // scroll-gebied dat niet te focussen is, is voor een toetsenbord-gebruiker
        // onbereikbaar (WCAG 2.2 SC 2.1.1) — maar een tabstop op elke tabel die
        // gewoon past, is enkel ruis in de tab-volgorde.
        {...(scrollable
          ? { tabIndex: 0, role: 'region' as const, 'aria-label': labels.scrollRegion }
          : {})}
      >
      <table className={styles.table}>
        <thead>
          <tr className={styles.headerRow}>
            {expandable && <th className={`${styles.headerCell} ${styles.expandCell}`} />}
            {selectable && (
              <th className={styles.headerCell} style={{ width: "48px" }}>
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={allSelected}
                  onChange={toggleAll}
                  aria-label={labels.selectAll}
                />
              </th>
            )}
            {showRowNumbers && (
              <th className={styles.headerCell} style={{ width: "48px" }}>
                {labels.rowNumberHeader}
              </th>
            )}
            {columns.map((col) => {
              const isSortable = sortable || col.sortable;
              return (
                <th
                  key={col.key}
                  className={`${styles.headerCell} ${isSortable ? styles.headerCellSortable : ""}`}
                  style={col.width ? { width: col.width } : undefined}
                  onClick={isSortable ? () => handleSort(col.key) : undefined}
                  aria-sort={
                    sortKey === col.key && sortDirection
                      ? sortDirection === "asc"
                        ? "ascending"
                        : "descending"
                      : undefined
                  }
                >
                  {col.label}
                  {renderSortIndicator(col)}
                </th>
              );
            })}
          </tr>
          {/*
            Filter-rij verschijnt alleen als minstens één kolom een
            `headerFilter`-control levert. Lege cellen voor kolommen
            zonder filter zodat de width-alignment intact blijft.
          */}
          {columns.some((c) => c.headerFilter !== undefined) && (
            <tr className={styles.filterRow}>
              {expandable && <th className={styles.filterCell} />}
              {selectable && <th className={styles.filterCell} />}
              {showRowNumbers && <th className={styles.filterCell} />}
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={styles.filterCell}
                  // Blocked van bubble-up: anders zou een klik in een
                  // filter-input ook de sort-handler van de header
                  // erboven triggeren (header-cell heeft cursor:pointer
                  // en onClick op sortable kolommen).
                  onClick={(e) => e.stopPropagation()}
                >
                  {col.headerFilter}
                </th>
              ))}
            </tr>
          )}
        </thead>

        <tbody>
          {pagedData.length === 0 ? (
            <tr>
              <td className={styles.emptyState} colSpan={totalCols}>
                {searchQuery.trim() !== "" ? labels.noMatch : labels.empty}
              </td>
            </tr>
          ) : (
            pagedData.map((row, idx) => {
              const originalIdx = data.indexOf(row);
              const expandKey = expandable ? expandable.rowKey(row) : "";
              const canExpand = expandable ? expandable.isExpandable(row) : false;
              const isExpanded = canExpand && expandedKeys.has(expandKey);
              return (
                <React.Fragment key={expandable ? expandKey : originalIdx}>
                  <tr
                    className={`${styles.bodyRow} ${selectedIndices.has(originalIdx) ? styles.selected : ""}`}
                  >
                    {expandable && (
                      <td className={`${styles.bodyCell} ${styles.expandCell}`}>
                        {canExpand && (
                          <button
                            type="button"
                            className={`${styles.expandBtn} ${isExpanded ? styles.expandBtnOpen : ""}`}
                            onClick={() => toggleExpanded(expandKey)}
                            aria-expanded={isExpanded}
                            aria-label={isExpanded ? expandable.collapseLabel : expandable.expandLabel}
                          >
                            <CaretRight size={14} weight="bold" />
                          </button>
                        )}
                      </td>
                    )}
                    {selectable && (
                      <td className={styles.bodyCell}>
                        <input
                          type="checkbox"
                          className={styles.checkbox}
                          checked={selectedIndices.has(originalIdx)}
                          onChange={() => toggleRow(originalIdx)}
                          aria-label={fmt(labels.selectRow, { n: originalIdx + 1 })}
                        />
                      </td>
                    )}
                    {showRowNumbers && (
                      <td className={styles.bodyCell}>{firstShown + idx}</td>
                    )}
                    {columns.map((col) => (
                      <td key={col.key} className={styles.bodyCell}>
                        {col.render
                          ? col.render(row[col.key], row)
                          : (row[col.key] ?? "")}
                      </td>
                    ))}
                  </tr>
                  {isExpanded && expandable && (
                    <tr className={styles.expandedRow}>
                      <td className={styles.expandedCell} colSpan={totalCols}>
                        {expandable.renderExpanded(row)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })
          )}
        </tbody>
      </table>
      </div>

      {(serverPagination || pageSize !== null || sortedData.length > 0) && (
        <div className={styles.footer}>
          <span className={styles.rangeText}>
            {fmt(labels.range, { from: firstShown, to: lastShown, total: totalRows })}
          </span>
          <div className={styles.footerSpacer} />
          {(serverPagination || pageSize !== null) && (
            <>
              <label className={styles.pageSizeLabel}>
                {labels.pageSize}
                <select
                  className={styles.pageSizeSelect}
                  value={serverPagination ? serverPagination.perPage : pageSize ?? 0}
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    if (serverPagination) {
                      serverPagination.onPerPageChange(next);
                    } else {
                      setPageSize(next);
                      setPageIndex(0);
                    }
                  }}
                >
                  {pageSizeOptions.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className={styles.pageBtn}
                onClick={() =>
                  serverPagination
                    ? serverPagination.onPageChange(Math.max(1, serverPagination.page - 1))
                    : setPageIndex((i) => Math.max(0, i - 1))}
                disabled={safePageIndex === 0}
                aria-label={labels.previousPage}
              >
                ←
              </button>
              <span className={styles.pageOfText}>
                {fmt(labels.pageOf, { current: safePageIndex + 1, total: totalPages })}
              </span>
              <button
                type="button"
                className={styles.pageBtn}
                onClick={() =>
                  serverPagination
                    ? serverPagination.onPageChange(Math.min(totalPages, serverPagination.page + 1))
                    : setPageIndex((i) => Math.min(totalPages - 1, i + 1))}
                disabled={safePageIndex >= totalPages - 1}
                aria-label={labels.nextPage}
              >
                →
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default DataTable;
